import { PublicClientApplication } from '@azure/msal-browser';
import azureConfig from '@/config/azure-config';
import api from '@/api';
import * as Sentry from '@sentry/vue';
import {
  setSentryUser,
  getUserContextForSentry,
  addUserToSentryScope,
} from '@/util/sentry-user';

export const AZURE_DOMAINS = [process.env.VUE_APP_AMREST_URL];

export default {
  namespaced: true,
  state: {
    accessToken: '',
    msalInstance: {},
    status: '',
    statusMap: {
      inProgress: 'in_progress',
      error: 'error',
      success: 'success',
    },
  },
  mutations: {
    SET_MSAL_INSTANCE(state) {
      state.msalInstance = new PublicClientApplication(azureConfig);
    },
    SET_AUTHENTICATION_STATUS(state, status) {
      state.status = status;
    },
    SET_ACTIVE_USER(state) {
      const [account] = state.msalInstance.getAllAccounts();
      if (account) state.msalInstance.setActiveAccount(account);
    },
  },
  getters: {
    azureAuthEnabled() {
      return AZURE_DOMAINS.includes(window.location.origin);
    },
    azureAuthInProgress(state) {
      return state.status && state.status !== state.statusMap.success;
    },
  },
  actions: {
    setupAzureIntegration({ commit }) {
      try {
        commit('SET_MSAL_INSTANCE');

        Sentry.addBreadcrumb({
          message: 'Azure MSAL instance created successfully',
          category: 'azure',
          level: 'info',
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { module: 'azure', action: 'setup' },
          extra: { azureConfig },
        });
        throw error;
      }
    },

    async authenticateWithAzure({ state, commit }) {
      commit('SET_AUTHENTICATION_STATUS', state.statusMap.inProgress);

      try {
        Sentry.addBreadcrumb({
          message: 'Starting Azure login redirect',
          category: 'azure',
          level: 'info',
        });

        await state.msalInstance.loginRedirect({});
      } catch (error) {
        commit('SET_AUTHENTICATION_STATUS', state.statusMap.error);

        Sentry.withScope((scope) => {
          scope.setTag('module', 'azure');
          scope.setTag('action', 'authenticate');
          scope.setTag('errorType', error.name || 'unknown');
          scope.setExtra('errorMessage', error.message);
          scope.setExtra('errorStack', error.stack);
          scope.setExtra('userAgent', navigator.userAgent);
          scope.setExtra('currentUrl', window.location.href);

          const userData = this.state?.user;
          if (userData) {
            const userContext = getUserContextForSentry(userData);
            if (userContext) {
              addUserToSentryScope(scope, userContext);
            }
          } else {
            scope.setUser({
              id: 'anonymous',
              username: 'attempting-azure-login',
            });
          }

          Sentry.captureException(error);
        });

        throw error;
      }
    },

    async setAzureUser({ state, commit, dispatch }) {
      try {
        Sentry.addBreadcrumb({
          message: 'Processing Azure redirect response',
          category: 'azure',
          level: 'info',
        });

        const redirectResponse =
          await state.msalInstance.handleRedirectPromise();

        if (redirectResponse) {
          const { idToken, account } = redirectResponse;

          Sentry.addBreadcrumb({
            message: 'Azure redirect response received',
            category: 'azure',
            level: 'info',
            data: {
              hasIdToken: !!idToken,
              hasAccount: !!account,
              accountId: account?.homeAccountId,
            },
          });

          if (!idToken) {
            const error = new Error(
              'Missing idToken in Azure redirect response',
            );

            Sentry.withScope((scope) => {
              scope.setTag('module', 'azure');
              scope.setTag('action', 'setUser');
              scope.setTag('errorType', 'missing_token');
              scope.setExtra('redirectResponse', { ...redirectResponse });
              scope.setExtra('accountInfo', { ...account });
              scope.setExtra('idToken', idToken);
              scope.setExtra('rawResponse', redirectResponse);

              const userData = this.state?.user;
              if (userData) {
                const userContext = getUserContextForSentry(userData);
                if (userContext) {
                  addUserToSentryScope(scope, userContext);
                }
              }

              Sentry.captureException(error);
            });

            throw error;
          }

          commit('SET_ACTIVE_USER');

          Sentry.addBreadcrumb({
            message: 'Calling backend API with Azure token',
            category: 'azure',
            level: 'info',
          });

          const { data } = await api.post('users/login/azure-ad', { idToken });

          commit('SET_AUTHENTICATION_STATUS', state.statusMap.success);

          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Raw backend response data',
            level: 'debug',
            data: { fullResponse: data },
          });

          if (!data.id || !data.email) {
            const error = new Error('Missing user data from Azure login');

            Sentry.withScope((scope) => {
              scope.setTag('module', 'azure');
              scope.setTag('action', 'setUser');
              scope.setTag('errorType', 'missing_user_data');
              scope.setTag('loginFlow', 'azure');
              scope.setTag('responseType', typeof data);
              scope.setExtra('fullResponseData', data);
              scope.setExtra('requestInfo', {
                url: window.location.href,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
              });
              scope.setExtra('msalState', {
                hasAccounts: state.msalInstance.getAllAccounts()?.length > 0,
                activeAccount: state.msalInstance.getActiveAccount()?.username,
                allAccounts: state.msalInstance.getAllAccounts(),
                activeAccountDetails: state.msalInstance.getActiveAccount(),
              });
              scope.setExtra('azureConfig', {
                ...azureConfig,
                env: process.env,
                origin: window.location.origin,
              });

              Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Backend returned response without user data',
                level: 'error',
                data: {
                  responseKeys: Object.keys(data || {}),
                  timestamp: new Date().toISOString(),
                },
              });

              Sentry.captureException(error);
            });

            throw error;
          }

          if (
            !data.id ||
            !data.email ||
            !data.firstName ||
            !data.lastName ||
            !data.role ||
            !data.type ||
            !data.systemRole ||
            !data.status
          ) {
            const error = new Error('Incomplete user data from Azure login');

            Sentry.withScope((scope) => {
              scope.setTag('module', 'azure');
              scope.setTag('action', 'setUser');
              scope.setTag('errorType', 'incomplete_user_data');
              scope.setTag('loginFlow', 'azure');
              scope.setTag(
                'emailDomain',
                data.email ? data.email.split('@')[1] : 'unknown',
              );

              scope.setExtra('responseData', data);

              scope.setExtra('requestInfo', {
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
              });

              scope.setExtra('azureState', {
                msalAccounts: state.msalInstance.getAllAccounts()?.length,
                activeAccount: state.msalInstance.getActiveAccount()?.username,
                authority: azureConfig.auth.authority,
                redirectUri: azureConfig.auth.redirectUri,
              });

              scope.setExtra('environment', {
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                windowSize: `${window.innerWidth}x${window.innerHeight}`,
                isOnline: navigator.onLine,
              });

              const missingFields = [
                !data.id && 'id',
                !data.email && 'email',
                !data.firstName && 'firstName',
                !data.lastName && 'lastName',
                !data.role && 'role',
                !data.type && 'type',
                !data.systemRole && 'systemRole',
                !data.status && 'status',
              ].filter(Boolean);

              Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Incomplete user data received from backend',
                level: 'error',
                data: {
                  missingFields,
                  presentFields: Object.keys(data || {}).filter(
                    (key) => !!data[key],
                  ),
                  timestamp: new Date().toISOString(),
                },
              });

              Sentry.captureException(error);
            });

            throw error;
          }

          try {
            const userContext = getUserContextForSentry(data);
            if (userContext) {
              setSentryUser(userContext);
            }
          } catch (sentryError) {
            Sentry.withScope((scope) => {
              scope.setTag('module', 'azure');
              scope.setTag('action', 'setUser');
              scope.setTag('errorType', 'sentry_set_user_failed');
              scope.setExtra('userData', { ...data });
              scope.setExtra('originalError', sentryError.message);
              Sentry.captureException(sentryError);
            });
          }

          Sentry.addBreadcrumb({
            message: 'Azure login completed successfully',
            category: 'azure',
            level: 'info',
            data: { userId: data.id },
          });

          dispatch('user/setUser', { data }, { root: true });
        }
      } catch (error) {
        commit('SET_AUTHENTICATION_STATUS', state.statusMap.error);

        const errorInfo = {
          tags: {
            module: 'azure',
            action: 'setUser',
            errorType: error.name || 'unknown',
          },
          extra: {
            errorMessage: error.message,
            errorStack: error.stack,
            userAgent: navigator.userAgent,
            currentUrl: window.location.href,
            msalAccounts: state.msalInstance.getAllAccounts?.(),
          },
        };

        if (error.response) {
          errorInfo.tags.errorSource = 'api';
          errorInfo.tags.httpStatus = error.response.status;
          errorInfo.extra.apiError = {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers,
            url: error.response.config?.url,
            method: error.response.config?.method,
          };
        } else if (error.request) {
          errorInfo.tags.errorSource = 'network';
          errorInfo.tags.networkErrorCode = error.code;
          errorInfo.extra.networkError = {
            timeout: error.timeout,
            code: error.code,
            url: error.config?.url,
            method: error.config?.method,
            isOnline: navigator.onLine,
            connectionType: navigator.connection?.type,
            effectiveType: navigator.connection?.effectiveType,
          };
        } else {
          errorInfo.tags.errorSource = 'client';
          errorInfo.extra.clientError = {
            browserInfo: {
              userAgent: navigator.userAgent,
              language: navigator.language,
              platform: navigator.platform,
              vendor: navigator.vendor,
            },
            windowInfo: {
              innerHeight: window.innerHeight,
              innerWidth: window.innerWidth,
              location: window.location.href,
            },
            timestamp: new Date().toISOString(),
          };
        }

        Sentry.captureException(error, errorInfo);

        throw error;
      }
    },
  },
};
